/*
 * useTableEvents Hook - React integration for TableEventBus
 * BUDB - Event-driven poker UI
 * Developed by GRUDGE STUDIO
 */

import { useEffect, useCallback, useState } from 'react';
import { tableEventBus, TableEvent, TableEventType } from '@/lib/table-event-bus';

export function useTableEvents(
  eventTypes: TableEventType | TableEventType[],
  callback: (event: TableEvent) => void
): void {
  useEffect(() => {
    const types = Array.isArray(eventTypes) ? eventTypes : [eventTypes];
    const unsubscribers = types.map(type => 
      tableEventBus.subscribe(type, callback)
    );
    
    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [eventTypes, callback]);
}

export function useAllTableEvents(callback: (event: TableEvent) => void): void {
  useEffect(() => {
    return tableEventBus.subscribeAll(callback);
  }, [callback]);
}

export interface CardAnimationState {
  isDealing: boolean;
  dealingPhase: 'idle' | 'preflop' | 'flop' | 'turn' | 'river' | 'showdown';
  newCards: any[];
  showdownActive: boolean;
}

export function useCardAnimations(tableId: string): CardAnimationState {
  const [state, setState] = useState<CardAnimationState>({
    isDealing: false,
    dealingPhase: 'idle',
    newCards: [],
    showdownActive: false
  });

  const handleEvent = useCallback((event: TableEvent) => {
    if (event.tableId !== tableId) return;

    switch (event.type) {
      case 'preDeal':
        setState(prev => ({
          ...prev,
          isDealing: true,
          dealingPhase: 'preflop',
          newCards: [],
          showdownActive: false
        }));
        break;
      case 'dealHole':
        setState(prev => ({
          ...prev,
          isDealing: true,
          dealingPhase: 'preflop',
          newCards: []
        }));
        setTimeout(() => {
          setState(prev => ({ ...prev, isDealing: false }));
        }, 1000);
        break;
      case 'dealFlop':
        setState(prev => ({
          ...prev,
          isDealing: true,
          dealingPhase: 'flop',
          newCards: event.data.cards
        }));
        setTimeout(() => {
          setState(prev => ({ ...prev, isDealing: false }));
        }, 1500);
        break;
      case 'dealTurn':
        setState(prev => ({
          ...prev,
          isDealing: true,
          dealingPhase: 'turn',
          newCards: [event.data.card]
        }));
        setTimeout(() => {
          setState(prev => ({ ...prev, isDealing: false }));
        }, 800);
        break;
      case 'dealRiver':
        setState(prev => ({
          ...prev,
          isDealing: true,
          dealingPhase: 'river',
          newCards: [event.data.card]
        }));
        setTimeout(() => {
          setState(prev => ({ ...prev, isDealing: false }));
        }, 800);
        break;
      case 'showdown':
        setState(prev => ({
          ...prev,
          dealingPhase: 'showdown',
          showdownActive: true
        }));
        break;
      case 'handComplete':
        setState({
          isDealing: false,
          dealingPhase: 'idle',
          newCards: [],
          showdownActive: false
        });
        break;
    }
  }, [tableId]);

  useTableEvents(
    ['preDeal', 'dealHole', 'dealFlop', 'dealTurn', 'dealRiver', 'showdown', 'handComplete'],
    handleEvent
  );

  return state;
}

export function usePotAnimation(tableId: string): { 
  potAmount: number; 
  isAnimating: boolean;
  previousAmount: number;
} {
  const [state, setState] = useState({
    potAmount: 0,
    isAnimating: false,
    previousAmount: 0
  });

  const handlePotUpdate = useCallback((event: TableEvent) => {
    if (event.tableId !== tableId) return;

    setState(prev => ({
      previousAmount: prev.potAmount,
      potAmount: event.data.to,
      isAnimating: true
    }));

    setTimeout(() => {
      setState(prev => ({ ...prev, isAnimating: false }));
    }, 600);
  }, [tableId]);

  useTableEvents('potUpdate', handlePotUpdate);

  return state;
}
